import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Send, MegaphoneIcon, Users, Zap, Eye, MousePointerClick, TrendingUp,
  Loader2, RefreshCw, ArrowLeft, CheckCircle2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import AutoBroadcastSection from "@/components/AutoBroadcastSection";
import DigestStatsSection from "@/components/DigestStatsSection";

function StatCard({ icon: Icon, label, value, sub, tone = "indigo" }) {
  const toneMap = {
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${toneMap[tone]}`}>
        <Icon size={18} />
      </div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-4">{label}</p>
      <p className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function CampaignRow({ c }) {
  const dt = c.created_at ? new Date(c.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "";
  const isAuto = c.trigger_kind === "auto";
  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`campaign-row-${c.id}`}>
      <td className="py-3 pr-4">
        <div className="flex items-start gap-2">
          {c.cover_image_url && (
            <img
              src={c.cover_image_url} alt="" loading="lazy"
              className="w-10 h-10 rounded object-cover shrink-0 border border-slate-200"
              onError={(e) => e.currentTarget.style.display = "none"}
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-medium text-slate-900 text-sm truncate max-w-[240px]">{c.title}</p>
              {isAuto && (
                <span className="inline-block px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 text-[9px] font-bold uppercase tracking-wide">
                  Auto
                </span>
              )}
              {c.coalesced_items > 1 && (
                <span className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-wide">
                  ×{c.coalesced_items}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 truncate max-w-[240px]">{c.body}</p>
            {c.event_type && (
              <p className="text-[10px] font-mono text-slate-400 mt-0.5 truncate max-w-[240px]">{c.event_type}</p>
            )}
          </div>
        </div>
      </td>
      <td className="py-3 pr-4 text-xs">
        <span className="inline-block px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">{c.category}</span>
        <span className="ml-1.5 inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">{c.segment}</span>
      </td>
      <td className="py-3 pr-4 text-sm text-slate-900 font-semibold text-right">{c.delivered}</td>
      <td className="py-3 pr-4 text-sm text-slate-700 text-right">{c.opened} <span className="text-xs text-slate-400">({c.open_rate}%)</span></td>
      <td className="py-3 pr-4 text-sm text-slate-700 text-right">{c.clicked} <span className="text-xs text-slate-400">({c.click_rate}%)</span></td>
      <td className="py-3 text-xs text-slate-500 whitespace-nowrap">{dt}</td>
    </tr>
  );
}

export default function AdminBroadcast() {
  const { user, loading: authLoading } = useAuth();
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewCount, setPreviewCount] = useState(null);

  // Form state
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("news");
  const [priority, setPriority] = useState("normal");
  const [segment, setSegment] = useState("all");
  const [inactiveDays, setInactiveDays] = useState(30);
  const [link, setLink] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, s, c] = await Promise.all([
        api.get("/notifications/meta"),
        api.get("/admin/notifications/stats"),
        api.get("/admin/notifications/campaigns?limit=50"),
      ]);
      setMeta(m.data);
      setStats(s.data);
      setCampaigns(c.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const doPreview = async () => {
    if (!title || !body) { toast.error("Titre et message obligatoires"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", {
        title, body, category, priority, segment, inactive_days: inactiveDays,
        link: link || null, expires_at: expiresAt || null, dry_run: true,
      });
      setPreviewCount(data.would_send_to);
      toast.success(`Aperçu : ${data.would_send_to} destinataires`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur de prévisualisation");
    } finally {
      setSending(false);
    }
  };

  const doSend = async () => {
    if (!title || !body) { toast.error("Titre et message obligatoires"); return; }
    if (!window.confirm(`Envoyer cette notification maintenant ?\n\nSegment: ${segment}\nCatégorie: ${category}\n\nCe message sera reçu par tous les utilisateurs concernés qui ont activé cette catégorie.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications/broadcast", {
        title, body, category, priority, segment, inactive_days: inactiveDays,
        link: link || null, expires_at: expiresAt || null, dry_run: false,
      });
      toast.success(`✅ Envoyée à ${data.delivered} utilisateurs`);
      setTitle(""); setBody(""); setLink(""); setExpiresAt(""); setPreviewCount(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  if (authLoading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-8" data-testid="admin-broadcast-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
            <Link to="/admin" className="hover:text-slate-800 flex items-center gap-1">
              <ArrowLeft size={12} /> Retour Admin
            </Link>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Admin · Communication</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2 flex items-center gap-3">
            <MegaphoneIcon size={36} /> Broadcast
          </h1>
          <p className="text-slate-600 mt-2">
            Envoie une notification ciblée à ta communauté. Chaque utilisateur peut se désabonner par catégorie.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="broadcast-refresh-btn">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </header>

      {/* KPIs */}
      {stats && (
        <section>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Stats globales · toute l'histoire</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Send} label="Envoyées" value={stats.alltime.sent} sub={`${stats.alltime.campaigns} campagne(s)`} tone="indigo" />
            <StatCard icon={Eye} label="Ouvertes" value={stats.alltime.opened} sub={`Taux : ${stats.alltime.open_rate}%`} tone="emerald" />
            <StatCard icon={MousePointerClick} label="Cliquées" value={stats.alltime.clicked} sub={`CTR : ${stats.alltime.click_rate}%`} tone="amber" />
            <StatCard icon={Users} label="Utilisateurs ciblés" value={stats.alltime.targeted} sub={`Provider : ${stats.provider}`} tone="slate" />
          </div>
          <p className="mt-3 text-xs text-slate-500 flex items-center gap-1">
            <TrendingUp size={12} /> 30 derniers jours : {stats.last_30d.sent} envoyées · {stats.last_30d.open_rate}% ouv. · {stats.last_30d.click_rate}% clic
          </p>
        </section>
      )}

      {/* Auto-broadcast — event-driven */}
      <AutoBroadcastSection onCampaignSent={load} />

      {/* Weekly digest — scheduler */}
      <DigestStatsSection />

      {/* Composer */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="broadcast-composer">
        <h2 className="font-display font-bold text-xl text-slate-900 mb-1">Nouvelle campagne</h2>
        <p className="text-sm text-slate-500 mb-6">Prépare, prévisualise le nombre de destinataires, puis envoie.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Label htmlFor="bc-title">Titre <span className="text-rose-500">*</span></Label>
            <Input
              id="bc-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex : Rentrée CROUS 2026 — Ouverture DSE le 15 janvier"
              maxLength={160} data-testid="bc-title-input" className="mt-1.5"
            />
            <p className="text-xs text-slate-400 mt-1">{title.length}/160</p>
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="bc-body">Message <span className="text-rose-500">*</span></Label>
            <Textarea
              id="bc-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Explique en 2-3 phrases. Sois clair, actionnable, sans jargon."
              maxLength={2000} data-testid="bc-body-input" className="mt-1.5"
            />
            <p className="text-xs text-slate-400 mt-1">{body.length}/2000</p>
          </div>

          <div>
            <Label>Catégorie <span className="text-rose-500">*</span></Label>
            <select
              value={category} onChange={(e) => setCategory(e.target.value)}
              className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="bc-category-select"
            >
              {(meta?.categories || []).map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-400 mt-1">Les utilisateurs peuvent se désabonner par catégorie.</p>
          </div>

          <div>
            <Label>Priorité</Label>
            <select
              value={priority} onChange={(e) => setPriority(e.target.value)}
              className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="bc-priority-select"
            >
              <option value="low">🟢 Faible</option>
              <option value="normal">🔵 Normale</option>
              <option value="high">🟡 Importante</option>
              <option value="urgent">🔴 Urgente (bypass des préférences)</option>
            </select>
          </div>

          <div>
            <Label>Segment</Label>
            <select
              value={segment} onChange={(e) => { setSegment(e.target.value); setPreviewCount(null); }}
              className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
              data-testid="bc-segment-select"
            >
              <option value="all">Tous les utilisateurs</option>
              <option value="new_signups">Nouveaux inscrits (30 j)</option>
              <option value="french">Étudiants français</option>
              <option value="international">Étudiants internationaux</option>
              <option value="premium">Utilisateurs Premium</option>
              <option value="free">Utilisateurs gratuits</option>
              <option value="inactive_days">Utilisateurs inactifs depuis X jours</option>
              <option value="notif_enabled">Notifs push activées uniquement</option>
            </select>
          </div>

          {segment === "inactive_days" && (
            <div>
              <Label htmlFor="bc-inactive">Inactifs depuis (jours)</Label>
              <Input
                id="bc-inactive" type="number" min={1} max={365}
                value={inactiveDays} onChange={(e) => setInactiveDays(parseInt(e.target.value || "30", 10))}
                data-testid="bc-inactive-days-input" className="mt-1.5"
              />
            </div>
          )}

          <div className={segment === "inactive_days" ? "" : "md:col-span-2"}>
            <Label htmlFor="bc-link">Lien (optionnel)</Label>
            <Input
              id="bc-link" value={link} onChange={(e) => setLink(e.target.value)}
              placeholder="/blog/dse-crous-2026 ou https://…"
              data-testid="bc-link-input" className="mt-1.5"
            />
          </div>

          <div className="md:col-span-2">
            <Label htmlFor="bc-expires">Expiration (optionnel)</Label>
            <Input
              id="bc-expires" type="datetime-local"
              value={expiresAt} onChange={(e) => setExpiresAt(e.target.value ? new Date(e.target.value).toISOString() : "")}
              data-testid="bc-expires-input" className="mt-1.5"
            />
            <p className="text-xs text-slate-400 mt-1">
              <Clock size={11} className="inline -mt-0.5 mr-1" />
              Après cette date, la notification disparaît des inbox utilisateurs.
            </p>
          </div>
        </div>

        {/* Preview badge */}
        {previewCount !== null && (
          <div className="mt-5 flex items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-200" data-testid="bc-preview-result">
            <CheckCircle2 size={18} className="text-indigo-600" />
            <p className="text-sm text-indigo-900">
              <span className="font-semibold">{previewCount}</span> utilisateur{previewCount > 1 ? "s" : ""} recevront cette notification (après filtrage des préférences).
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={doPreview}
            disabled={sending}
            data-testid="bc-preview-btn"
            className="gap-1.5"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
            Aperçu (dry run)
          </Button>
          <Button
            onClick={doSend}
            disabled={sending || !title || !body}
            data-testid="bc-send-btn"
            className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 flex-1 sm:flex-initial"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            Envoyer maintenant
          </Button>
        </div>
      </section>

      {/* Campaign history */}
      <section className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8" data-testid="broadcast-history">
        <h2 className="font-display font-bold text-xl text-slate-900 mb-4">Historique des campagnes</h2>
        {campaigns.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Aucune campagne envoyée pour l'instant.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="campaigns-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-2 pr-4 font-medium">Contenu</th>
                  <th className="py-2 pr-4 font-medium">Cat. / Segment</th>
                  <th className="py-2 pr-4 font-medium text-right">Livrées</th>
                  <th className="py-2 pr-4 font-medium text-right">Ouvertes</th>
                  <th className="py-2 pr-4 font-medium text-right">Clics</th>
                  <th className="py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => <CampaignRow key={c.id} c={c} />)}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
